import { Action, Dispatch } from 'redux';
import _ from 'lodash';
import { IAppState } from '../reducers';
import {
  AssetHit, IGameSearchResponse, ISearchParams, searchAssets,
} from '../../api/vault/searchEngine';
import { selectUserRole, selectUserOrgsSecurityProfilesIds, KeyRoleUserVaultRoles } from '../user/userOrgRole';
import { StreamStatus } from '../../enums';

export interface IState {
  searchResults: IGameSearchResponse | null;
  isLoading: boolean;
  error: string | null;
  interval: NodeJS.Timeout | null;
}

export enum ActionType {
  SetSearchResults = 'LiveEventsList.SetSearchResults',
  SetLoading = 'LiveEventsList.SetLoading',
  SetError = 'LiveEventsList.SetError',
  SetInterval = 'LiveEventsList.SetInterval',
}

export interface ILiveEventsListAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  searchResults: null,
  isLoading: false,
  error: null,
  interval: null,
};

export default function reducer(state: IState = INITIAL_STATE, action: ILiveEventsListAction): IState {
  switch (action.type) {
    case ActionType.SetSearchResults:
      return {
        ...state,
        searchResults: action.payload.searchResults,
        isLoading: false,
        error: null,
      };
    case ActionType.SetLoading:
      return {
        ...state,
        isLoading: action.payload.isLoading,
      };
    case ActionType.SetError:
      return {
        ...state,
        error: action.payload.error,
        isLoading: false,
      };
    case ActionType.SetInterval:
      return {
        ...state,
        interval: action.payload.interval,
      };
    default:
      return state;
  }
}

export const setSearchResults = (searchResults: IGameSearchResponse) => ({
  type: ActionType.SetSearchResults,
  payload: { searchResults },
});

export const setLoading = (isLoading: boolean) => ({
  type: ActionType.SetLoading,
  payload: { isLoading },
});

export const setError = (error: string) => ({
  type: ActionType.SetError,
  payload: { error },
});

export const searchLiveAssets = (fromInterval: boolean = false) => async (dispatch: Dispatch<ILiveEventsListAction>, getState: () => IAppState) => {
  try {
    const state: IAppState = getState();
    const {
      liveEventsList: {
        searchResults: oldSearchResults,
        interval,
      },
      search: {
        queryParams: {
          sortField,
          sortOrder,
          size,
          page,
        },
      },
      filterAssets,
    } = state;

    // Restart interval if not called from interval
    if (!fromInterval && interval) {
      clearInterval(interval);
      startBackgroundSearchInterval()(dispatch, getState);
    }

    const userOrgs = selectUserOrgsSecurityProfilesIds(state);
    const userRole = selectUserRole(state);
    const isSuperUser = userRole[KeyRoleUserVaultRoles].some((x) => x.roleName === 'vaultSuperAdmin');

    let queryString = '';
    if (filterAssets?.liveAssetFilters?.textSearch) {
      queryString = filterAssets.liveAssetFilters.textSearch;
    }

    let streamStatusFilter = false;
    const activeFilters = Object.entries(filterAssets.liveAssetFilters).filter(([key, value]) => key !== 'textSearch' && value != null && value !== '' && !(Array.isArray(value) && value.length === 0));
    activeFilters.forEach(([key, value], index) => {
      if (key === 'securityProfiles' && value) {
        activeFilters[index] = ['securityProfiles', [value.value]];
      } else if (key === 'streamStatus' && value) {
        activeFilters[index] = ['streamStatus', [value]];
        streamStatusFilter = true;
      }
    });

    let filters = [
      ['type', ['Live Video', 'Video']],
      ['gameDate', [
        new Date(new Date().getTime() - (36e2 * 24 * 7 * 1000)).toISOString(), // 7 days ago
      ]],
    ];
    // Every status an event can hold has to be listed, or "All" silently hides
    // events that are in one of the missing ones - AwaitingSource events were
    // invisible here, which is the worst case for a status whose whole purpose is
    // telling an operator to go and fix the feed.
    if (!streamStatusFilter) {
      filters.push(['streamStatus', [
        StreamStatus.Complete, StreamStatus.Live, StreamStatus.Error, StreamStatus.Initializing,
        StreamStatus.Scheduled, StreamStatus.Processing, StreamStatus.Restarting,
        StreamStatus.AwaitingSource, StreamStatus.SourceEnded,
      ]]);
    }
    if (!isSuperUser) filters.push(['owner', userOrgs]);
    filters = filters.concat(activeFilters);

    const searchParams: ISearchParams = {
      queryString,
      size,
      page,
      filters,
      sortField,
      sortOrder,
    };

    const response = await searchAssets(searchParams);

    if (!oldSearchResults || oldSearchResults?.hits?.hits && response?.data?.data?.hits?.hits
      && !_.isEqual(oldSearchResults.hits.hits, response.data.data.hits.hits)) {
      dispatch(setSearchResults(response.data.data));
    }
  } catch (error) {
    console.log(error);
  }
};

export const startBackgroundSearchInterval = () => (dispatch: Dispatch<ILiveEventsListAction>, getState: () => IAppState) => {
  const interval = setInterval(() => {
    searchLiveAssets(true)(dispatch, getState);
  }, 15000); // every 15 seconds

  dispatch({
    type: ActionType.SetInterval,
    payload: { interval },
  });
};

export const clearBackgroundSearchInterval = () => async (dispatch: Dispatch<ILiveEventsListAction>, getState: () => IAppState) => {
  const state: IAppState = getState();
  const { liveEventsList: { interval } } = state;
  if (interval) {
    clearInterval(interval);
    dispatch({
      type: ActionType.SetInterval,
      payload: { interval: null },
    });
  }
};

// Selectors
export const selectSearchResults = (state: IAppState): IGameSearchResponse | null => state.liveEventsList?.searchResults || null;
export const selectLiveEvents = (state: IAppState): AssetHit[] => state.liveEventsList?.searchResults?.hits?.hits || [];
